import { ESTIMATE_PC_MSG, RESTORE_MSG, TAKEOFF_MSG, TAKEOFF_PHOTO, evalTakeoff, prettyFormula, takeoffQty, takeoffTotal, type Estimate, type LineInput, type QuantitySource, type Takeoff, type TakeoffList } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { api, apiUpload, ApiError, messageOf } from "../api.js";
import { t } from "../labels.js";
import { fitWithin } from "../lib/image.js";
import { useSaveQueue } from "../lib/saveQueue.jsx";
import { LeaveConfirm } from "./BackButton.jsx";
import { PlaceSelect, type AxesLists } from "./EstimateAxes.jsx";
import { UnitInput } from "./UnitInput.jsx";

/* ============================================================================
   拾い出し（積算。#130、docs/47 §2.2）。見積の行の数量の欄から開く、右からの引き出し。
   ・候補を選ぶ道具ではなく編集の道具（入力欄を置く）。式の欄は打つたびに答えを横に出す（evalTakeoff＝DB と同じ決まり）
   ・数量は DB が決める：足す・直す・消すたびにサーバーの答え（拾い出しの一覧と見積）で紙を描き直す。画面では合計を保存しない
   ・写真はこの端末で長辺 1600px に縮めてから送る（サーバーでは縮めない）
   ・出した回でも拾い出しは足せる（数量は変わらない。次の回で入る）
   ・欄の直し（場所・式・単位・メモ）は共通の仕組み（lib/saveQueue.tsx の useSaveQueue、#179 の3）：1本ずつ・送っている最中に離れた欄の直しも
     捨てずに続けて送る・断られた直しは残す・離れるとき（‹・リンク・戻る）は送り終えてから・「閉じる」も送り終えてから（送れなければ「続ける／破棄」）。
     足す・消す・写真・行に入れる／外すは、欄の直しを送り終えてから送る（順番が入れ替わらない）
   ============================================================================ */

type Row = LineInput;
/** 書きかえの答え（POST／PUT／DELETE …/takeoffs） */
export type TakeoffAnswer = { takeoffs: TakeoffList; estimate: Estimate };

/** 写真をこの端末で描き直して小さくする（長辺 1600px・JPEG）。撮った場所などの情報は描き直すと消える */
async function shrinkPhoto(file: Blob): Promise<Blob> {
  let bmp: ImageBitmap;
  try {
    bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    throw new Error(TAKEOFF_MSG.photoBroken);
  }
  try {
    const draw = async (quality: number): Promise<Blob> => {
      const { width, height } = fitWithin(bmp.width, bmp.height, TAKEOFF_PHOTO.maxPx);
      const c = document.createElement("canvas");
      c.width = width;
      c.height = height;
      c.getContext("2d")!.drawImage(bmp, 0, 0, width, height);
      const blob = await new Promise<Blob | null>((resolve) => c.toBlob(resolve, "image/jpeg", quality));
      if (!blob) throw new Error(TAKEOFF_MSG.photoBroken);
      return blob;
    };
    let out = await draw(TAKEOFF_PHOTO.quality);
    if (out.size > TAKEOFF_PHOTO.maxBytes) out = await draw(0.6);
    return out;
  } finally {
    bmp.close();
  }
}

/** 式の欄の横の答え（打つたびに）。読めない式は「式が読めません」。単位はとなりの欄 */
function Answer({ formula }: { formula: string }) {
  if (!formula.trim()) return <span className="tkans muted" />;
  const q = evalTakeoff(formula);
  return (
    <span className={"tkans" + (q == null ? " ng" : "")} data-testid="takeoff-answer">
      {q == null ? TAKEOFF_MSG.noFormula : `＝ ${takeoffQty(q)}`}
    </span>
  );
}

export function TakeoffDrawer({
  estimateId, lineId, rows, qtyOf, lists, issued, onClose, run, reloadTick, onSource, onAskNewPlace,
}: {
  estimateId: string;
  /** 開いている行（null＝行に入れていない拾い出しだけを見る） */
  lineId: string | null;
  rows: Row[];
  /** いま紙に出ている数量（takeoff の行はサーバーが決めた値） */
  qtyOf: (r: Row) => number;
  lists: AxesLists;
  /** 出したあとの紙（数量は変わらない。拾い出しは足せる） */
  issued: boolean;
  onClose: () => void;
  /**
   * 書きかけを送ってから API を呼び、答えで紙を描き直す（Estimate.tsx が渡す）。断られたら false。
   * call は見積のいまの版を受け取って ?version= に付ける（2026-10-13：ほかで変わっていたら 409 → 見積の画面の「読み直しますか？」）
   */
  run: (call: (version: string) => Promise<TakeoffAnswer>) => Promise<TakeoffAnswer | undefined>;
  /** 見積の画面で読み直しが済んだ回数（増えたら一覧を読み直し、ほかで変わっていて断られた欄の直しを送り直す） */
  reloadTick: number;
  /** 数量の出どころを切り替える（手で打つに戻す／拾い出しの合計にする）。画面の行を直して自動保存で送る */
  onSource: (lineId: string, source: QuantitySource) => void;
  onAskNewPlace: (done: (placeId: string) => void) => void;
}) {
  const qc = useQueryClient();
  const key = ["takeoffs", estimateId];
  /** 見積の版（?version=） */
  const v = (version: string) => `?version=${encodeURIComponent(version)}`;
  const q = useQuery({ queryKey: key, queryFn: () => api<TakeoffList>("GET", `/estimates/${estimateId}/takeoffs`) });
  const line = lineId ? rows.find((r) => r.id === lineId) : undefined;
  const items = q.data?.items ?? [];
  const mine = line ? items.filter((t) => t.lineId === line.id) : [];
  const loose = items.filter((t) => !t.lineId);
  const placeLabel = (id: string | null): string => {
    if (!id) return "";
    const p = lists.places.find((x) => x.id === id);
    if (!p) return "";
    const parent = p.parentId ? lists.places.find((x) => x.id === p.parentId) : undefined;
    return parent ? `${parent.name} ＞ ${p.name}` : p.name;
  };

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  /** まだ送っていない欄の直し（拾い出しの id → 直した欄。欄を離れるたびに重ねる。#179 の3） */
  const pending = useRef(new Map<string, Record<string, unknown>>());
  const tq = useSaveQueue({
    send: async () => {
      while (pending.current.size) {
        const [id, body] = pending.current.entries().next().value as [string, Record<string, unknown>];
        pending.current.delete(id);
        try {
          const r = await run((ver) => api<TakeoffAnswer>("PUT", `/estimates/${estimateId}/takeoffs/${id}${v(ver)}`, body));
          if (!r) throw new Error(RESTORE_MSG.unsaved); // 見積の書きかけが送れていない
          qc.setQueryData(key, r.takeoffs);
        } catch (e) {
          // 断られた直しは残す（送っている間に同じ拾い出しをまた直していたら、その分を上に重ねる）
          pending.current.set(id, { ...body, ...(pending.current.get(id) ?? {}) });
          throw e;
        }
      }
    },
    onError: (e) =>
      // ほかで変わっていた：見積の画面が「読み直しますか？」を開く。読み直したら送り直す（下の reloadTick）
      e instanceof ApiError && e.code === "ESTIMATE_STALE"
        ? { msg: ESTIMATE_PC_MSG.staleSaving, then: "stop" }
        : { msg: e instanceof Error && !(e as { problem?: unknown }).problem ? e.message : messageOf(e) },
    wait: 0,
  });
  // 見積の画面で読み直しが済んだ：一覧を取り直し、断られたままの欄の直しを最新の版で送り直す
  useEffect(() => {
    if (!reloadTick) return;
    void qc.invalidateQueries({ queryKey: key });
    if (tq.isDirty()) void tq.flush();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTick]);
  /** 欄の直しを送る列に入れる（送っている最中でも捨てない） */
  const queuePatch = (tk: Takeoff, body: Record<string, unknown>) => {
    pending.current.set(tk.id, { ...(pending.current.get(tk.id) ?? {}), ...body });
    tq.mark(true);
  };
  const box = useRef<HTMLElement | null>(null);
  /** 閉じる：打ちかけの欄を送り、送り終えてから閉じる。送れなければ「続ける／破棄」 */
  const [closeAsk, setCloseAsk] = useState(false);
  const close = async () => {
    const el = document.activeElement;
    if (el instanceof HTMLElement && box.current?.contains(el)) el.blur(); // 欄を離れたときに送る形＝閉じる前に送る
    if (await tq.settle()) onClose();
    else setCloseAsk(true);
  };
  const closeRef = useRef(close);
  closeRef.current = close;
  /** 足す欄 */
  const [draft, setDraft] = useState<{ placeId: string | null; formula: string; unit: string; memo: string }>({ placeId: null, formula: "", unit: line?.unit ?? "", memo: "" });
  useEffect(() => {
    setDraft((d) => ({ ...d, unit: line?.unit ?? "", formula: "", memo: "" }));
    setErr(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineId]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.isComposing) void closeRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /** API を呼んで、答えの一覧をこの引き出しに入れる（欄の直しを送り終えてから。順番が入れ替わらない） */
  const act = async (call: (version: string) => Promise<TakeoffAnswer>): Promise<boolean> => {
    if (busy) return false;
    setBusy(true);
    setErr(undefined);
    try {
      if (!(await tq.settle())) return false; // 欄の直しが送れていない（上の帯の文のまま）
      const r = await run(call);
      if (!r) return false;
      qc.setQueryData(key, r.takeoffs);
      return true;
    } catch (e) {
      setErr(e instanceof Error && !(e as { problem?: unknown }).problem ? e.message : messageOf(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const add = async () => {
    if (!draft.formula.trim() && !draft.memo.trim()) {
      setErr(TAKEOFF_MSG.empty);
      document.getElementById("tk_formula")?.focus();
      return;
    }
    const ok = await act((ver) => api<TakeoffAnswer>("POST", `/estimates/${estimateId}/takeoffs${v(ver)}`, { lineId: line?.id ?? null, placeId: draft.placeId, formula: draft.formula, unit: draft.unit.trim(), memo: draft.memo.trim() }));
    if (ok) {
      setDraft((d) => ({ ...d, formula: "", memo: "" }));
      document.getElementById("tk_formula")?.focus();
    }
  };
  /** 行に入れる／外す（押したらすぐ。欄の直しは queuePatch） */
  const patch = (t: Takeoff, body: Record<string, unknown>) => act((ver) => api<TakeoffAnswer>("PUT", `/estimates/${estimateId}/takeoffs/${t.id}${v(ver)}`, body));
  const remove = (t: Takeoff) => act((ver) => api<TakeoffAnswer>("DELETE", `/estimates/${estimateId}/takeoffs/${t.id}${v(ver)}`));
  const photo = async (t: Takeoff, file: File | undefined) => {
    if (!file) return;
    const jpeg = await shrinkPhoto(file);
    await act((ver) => apiUpload<TakeoffAnswer>(`/estimates/${estimateId}/takeoffs/${t.id}/photo${v(ver)}`, jpeg, "image/jpeg"));
  };
  const dropPhoto = (t: Takeoff) => act((ver) => api<TakeoffAnswer>("DELETE", `/estimates/${estimateId}/takeoffs/${t.id}/photo${v(ver)}`));

  const total = takeoffTotal(mine.map((t) => t.quantity));
  const unit = mine.find((t) => t.unit)?.unit || line?.unit || "";
  const isDetail = line?.role === "detail";

  const item = (t: Takeoff, where: "line" | "loose") => (
    <TakeoffItem
      // 答えで描き直しても欄を作り直さない（打ちかけの欄を消さない。#179 の3。答えの値は TakeoffItem が直していない欄にだけ入れる）
      key={t.id}
      t={t}
      lists={lists}
      placeLabel={placeLabel(t.placeId)}
      busy={busy}
      onPatch={(body) => queuePatch(t, body)}
      onRemove={() => void remove(t)}
      onPhoto={(f) => void photo(t, f)}
      onDropPhoto={() => void dropPhoto(t)}
      onAskNewPlace={onAskNewPlace}
      {...(where === "loose" && isDetail ? { onAttach: () => void patch(t, { lineId: line!.id }) } : {})}
      {...(where === "line" ? { onDetach: () => void patch(t, { lineId: null }) } : {})}
    />
  );

  return (
    <aside ref={box} className="picker open tkdrawer" role="dialog" aria-label={t(TAKEOFF_MSG.takeoff)} data-testid="takeoff-drawer">
      <div className="ph">
        <b>{t(TAKEOFF_MSG.takeoff)}</b>
        <span className="hint" data-testid="takeoff-line">{line ? line.name || t("（{lineName}なし）") : "行に入れていないもの"}</span>
        <span className="sp" />
        <button type="button" className="x" onClick={() => void close()}>閉じる</button>
      </div>
      <div className="pl">
        {loose.length ? (
          <div data-testid="takeoff-loose">
            <div className="pg">
              <b>{TAKEOFF_MSG.unassigned.replace("{n}", String(loose.length))}</b>
            </div>
            {loose.map((t) => item(t, "loose"))}
          </div>
        ) : null}
        {line ? (
          <div data-testid="takeoff-mine">
            <div className="pg">この行の{t(TAKEOFF_MSG.takeoff)} <b className="num">{mine.length}</b></div>
            {mine.length ? (
              <div className="tkgrid tkhead" aria-hidden="true">
                <span>場所</span><span>{TAKEOFF_MSG.formula}</span><span>数量</span><span>単位</span>
              </div>
            ) : null}
            {mine.map((t) => item(t, "line"))}
            {mine.length === 0 && !q.isPending ? <div className="pempty">まだありません。下の欄から足します。</div> : null}
            {/* #131 の2回目：拾い出しが 0 本なのに数量の出どころが「拾い出しの合計」のまま（古いデータ・保存の前）でも、手で打つに戻せる */}
            {isDetail && !issued && line.quantitySource === "takeoff" && !mine.some((t) => t.quantity != null) ? (
              <div className="tksum" data-testid="takeoff-sum">
                <span className="muted">行の数量は拾い出しの合計（{qtyOf(line) || 0}）</span>
                <button type="button" className="small" data-testid="takeoff-to-manual" disabled={busy} onClick={() => onSource(line.id, "manual")}>{TAKEOFF_MSG.toManual}</button>
              </div>
            ) : null}
            {isDetail && mine.some((t) => t.quantity != null) ? (
              <div className="tksum" data-testid="takeoff-sum">
                <span>合計 <b className="num">{total ? takeoffQty(total) : "0"}</b> {unit}</span>
                {issued ? (
                  <span className="muted">出した回の数量（{qtyOf(line) || 0}）は変わりません。次の回で入ります。</span>
                ) : line.quantitySource === "takeoff" ? (
                  <>
                    <span className="muted">行の数量に入っています</span>
                    <button type="button" className="small" data-testid="takeoff-to-manual" disabled={busy} onClick={() => onSource(line.id, "manual")}>{TAKEOFF_MSG.toManual}</button>
                  </>
                ) : (
                  <>
                    <span className="muted">行の数量は手入力（{qtyOf(line) || 0}）</span>
                    <button type="button" className="small ai" data-testid="takeoff-to-takeoff" disabled={busy} onClick={() => onSource(line.id, "takeoff")}>{TAKEOFF_MSG.toTakeoff}</button>
                  </>
                )}
              </div>
            ) : null}
          </div>
        ) : null}
        {q.isError ? <div className="pempty">{messageOf(q.error)}</div> : null}
      </div>
      <form
        className="pf tkadd"
        data-notice-above=""
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void add();
        }}
      >
        <div className="tkgrid">
          <PlaceSelect id="tk_place" value={draft.placeId} lists={lists} onChange={(v) => setDraft((d) => ({ ...d, placeId: v }))} onAskNew={onAskNewPlace} />
          <input className="ce num" id="tk_formula" value={draft.formula} autoComplete="off" aria-label={TAKEOFF_MSG.formula} placeholder={TAKEOFF_MSG.formula} onChange={(e) => setDraft((d) => ({ ...d, formula: e.target.value }))} onKeyDown={(e) => (e.nativeEvent.isComposing || e.keyCode === 229) && e.key === "Enter" && e.preventDefault()} />
          <Answer formula={draft.formula} />
          <UnitInput id="tk_unit" className="ce" value={draft.unit} placeholder="単位" onChange={(v) => setDraft((d) => ({ ...d, unit: v }))} />
        </div>
        <div className="tkrow">
          <input className="ce tkmemo" id="tk_memo" value={draft.memo} aria-label="メモ" placeholder="メモ" onChange={(e) => setDraft((d) => ({ ...d, memo: e.target.value }))} onKeyDown={(e) => (e.nativeEvent.isComposing || e.keyCode === 229) && e.key === "Enter" && e.preventDefault()} />
          <button type="submit" className="btn primary" disabled={busy} data-testid="takeoff-add">{TAKEOFF_MSG.add}</button>
        </div>
        {err || tq.state.kind === "error" ? (
          <div className="err" role="alert" data-testid="takeoff-error">
            {err ?? (tq.state.kind === "error" ? tq.state.msg : "")}
          </div>
        ) : null}
      </form>
      {tq.dialog}
      {closeAsk ? (
        <LeaveConfirm
          onStay={() => setCloseAsk(false)}
          onLeave={() => {
            setCloseAsk(false);
            pending.current.clear();
            tq.discard();
            onClose();
          }}
        />
      ) : null}
    </aside>
  );
}

/** 拾い出しの1行（場所・式・＝数量・単位・メモ・写真・消す）。式・単位・メモは欄を離れたときに送る */
function TakeoffItem({
  t, lists, placeLabel, busy, onPatch, onRemove, onPhoto, onDropPhoto, onAttach, onDetach, onAskNewPlace,
}: {
  t: Takeoff;
  lists: AxesLists;
  placeLabel: string;
  busy: boolean;
  onPatch: (body: Record<string, unknown>) => void;
  onRemove: () => void;
  onPhoto: (f: File | undefined) => void;
  onDropPhoto: () => void;
  onAttach?: () => void;
  onDetach?: () => void;
  onAskNewPlace: (done: (placeId: string) => void) => void;
}) {
  const [formula, setFormula] = useState(t.formula);
  const [memo, setMemo] = useState(t.memo);
  const [unit, setUnit] = useState(t.unit);
  /** 単位の候補を Enter で選んだ直後の blur でも選んだ値を送る（state の更新を待たない） */
  const unitNow = useRef(t.unit);
  /** 前に受け取った値と、送った値（#179 の3：答えが着いたとき、直していない欄・送った値のままの欄だけ答えの値にする＝打ちかけを消さない） */
  const seen = useRef({ formula: t.formula, memo: t.memo, unit: t.unit });
  const sent = useRef<{ formula?: string; memo?: string; unit?: string }>({});
  useEffect(() => {
    const was = seen.current;
    const follow = (cur: string, k: "formula" | "memo" | "unit") => (cur === was[k] || cur === sent.current[k] ? t[k] : cur);
    setFormula((cur) => follow(cur, "formula"));
    setMemo((cur) => follow(cur, "memo"));
    setUnit((cur) => {
      const next = follow(cur, "unit");
      unitNow.current = next;
      return next;
    });
    seen.current = { formula: t.formula, memo: t.memo, unit: t.unit };
  }, [t.formula, t.memo, t.unit]); // eslint-disable-line react-hooks/exhaustive-deps
  const sendIfChanged = () => {
    const body: Record<string, unknown> = {};
    if (formula.trim() !== t.formula) body.formula = formula;
    if (memo.trim() !== t.memo) body.memo = memo.trim();
    if (unitNow.current.trim() !== t.unit) body.unit = unitNow.current.trim();
    if (!Object.keys(body).length) return;
    sent.current = { ...sent.current, ...(body.formula !== undefined ? { formula } : {}), ...(body.memo !== undefined ? { memo } : {}), ...(body.unit !== undefined ? { unit: unitNow.current } : {}) };
    onPatch(body);
  };
  const onEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter" || e.nativeEvent.isComposing || e.keyCode === 229) return;
    e.preventDefault();
    (e.target as HTMLInputElement).blur();
  };
  return (
    <div className="tkitem" data-testid="takeoff-item">
      <div className="tkgrid">
        <PlaceSelect id={`tk_place_${t.id}`} value={t.placeId} lists={lists} onChange={(v) => onPatch({ placeId: v })} onAskNew={onAskNewPlace} />
        <input className="ce num" value={formula} autoComplete="off" placeholder="式" aria-label={`式${placeLabel ? `（${placeLabel}）` : ""}`} data-testid="takeoff-formula" onChange={(e) => setFormula(e.target.value)} onBlur={sendIfChanged} onKeyDown={onEnter} />
        <Answer formula={formula} />
        <UnitInput
          className="ce"
          value={unit}
          placeholder="単位"
          onChange={(v) => {
            unitNow.current = v;
            setUnit(v);
          }}
          onBlur={sendIfChanged}
          onKeyDown={onEnter}
        />
      </div>
      <div className="tkrow acts">
        <input className="ce tkmemo" value={memo} aria-label="メモ" placeholder="メモ" data-testid="takeoff-memo" onChange={(e) => setMemo(e.target.value)} onBlur={sendIfChanged} onKeyDown={onEnter} />
        {t.photoUrl ? (
          <a href={t.photoUrl} target="_blank" rel="noreferrer" className="tkphoto" title="写真を表示">
            <img src={t.photoUrl} alt={`${placeLabel || "拾い出し"}の写真`} data-testid="takeoff-photo" />
          </a>
        ) : null}
      </div>
      <div className="tkrow acts">
        {/* ファイルを選ぶのは入力欄の <label>（display:none の input を click() で開かない＝Safari。docs/10 §5.10） */}
        <label className="small filebtn">
          写真選択
          <input type="file" accept="image/*" className="vh" data-testid="takeoff-photo-input" onChange={(e) => { onPhoto(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
        <label className="small filebtn">
          撮る
          <input type="file" accept="image/*" capture="environment" className="vh" onChange={(e) => { onPhoto(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
        {t.photoUrl ? <button type="button" className="small" disabled={busy} onClick={onDropPhoto}>写真削除</button> : null}
        <span className="sp" />
        {onAttach ? <button type="button" className="small ai" disabled={busy} data-testid="takeoff-attach" onClick={onAttach}>{TAKEOFF_MSG.attach}</button> : null}
        {onDetach ? <button type="button" className="small" disabled={busy} data-testid="takeoff-detach" onClick={onDetach}>{TAKEOFF_MSG.detach}</button> : null}
        <button type="button" className="small shu" disabled={busy} data-testid="takeoff-remove" onClick={onRemove}>削除</button>
      </div>
      {t.formula && t.quantity == null ? <div className="hint ng">{TAKEOFF_MSG.noFormula}（{prettyFormula(t.formula)}）</div> : null}
    </div>
  );
}
